import React, { useState, useEffect } from 'react';
import { translate } from '../../../Services/translation';
import { BCTextInput } from '../BCTextInput';
import { BCTabContainer } from './BCTabContainer';
import { useBCApi } from '../hooks/BCApi';
import { string, object } from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { setError } from '../../../Actions/Person.action';
import { updatePassword } from '../../../Services/BusinessCentral/BusinessCentral.service';

export const Company = () => {
    const [company, setCompany] = useState({});
    const [showSuccess, setShowSuccess] = useState(false);
    const [showSuccessPassword, setShowSuccessPassword] = useState(false);
    const [loaderPassword, setLoaderPassword] = useState(false);
    const errors = useSelector((state) => state.myPage.persons.errors) || {};
    const dispatch = useDispatch();
    const { status, value } = useBCApi('/api/bcmypages/company', true);
    const { status: userStatus, execute } = useBCApi(
        '/api/bcmypages/users',
        true
    );
    const [password, setPassword] = useState({
        password: '',
        confirmPassword: '',
    });

    const phoneRegExp = /^(\+?)\(?\d{2,3}\)? *-? *\d{2,4} *-? *\d{2,4} *-? *\d{2,4}$/;
    const contactPersonSchema = object().shape({
        firstName: string().required(translate(`validation.required`)),
        lastName: string().required(translate(`validation.required`)),
        phone: string()
            .required(translate(`validation.required`))
            .matches(phoneRegExp, translate(`validation.phone`)),
    });

    const passwordSchema = object().shape({
        password: string().required(translate(`validation.required`)),
        confirmPassword: string()
            .required(translate(`validation.required`))
            .matches(
                password.password,
                translate(`changepassword.passworddoesnotmatch`)
            ),
    });

    useEffect(() => {
        if (status === 'success') {
            setCompany(value);
        }
    }, [status, value]);

    const onChange = (propName, value) => {
        setCompany((prevState) => {
            return {
                ...prevState,
                contactPerson: {
                    ...prevState.contactPerson,
                    [propName]: value,
                },
            };
        });
    };

    const onSubmit = () => {
        contactPersonSchema
            .validate(company.contactPerson)
            .then(() => {
                execute('PUT', company.contactPerson);
            })
            .finally(() => {
                setShowSuccess(true);
            })
            .catch((error) => dispatch(setError(error)));
    };

    const onChangePassword = (propName, value) => {
        setPassword((prevState) => {
            return {
                ...prevState,
                [propName]: value,
            };
        });
    };

    const onSubmitPassword = () => {
        passwordSchema
            .validate(password)
            .then(() => {
                setLoaderPassword(true);
                updatePassword(password.password, password.confirmPassword)
                    .then(() => {
                        setShowSuccessPassword(true);
                    })
                    .finally(() => {
                        setLoaderPassword(false);
                        errors = null;
                    })
                    .catch((error) => {
                        dispatch(setError(error));
                    });
            })
            .catch((error) => {
                dispatch(setError(error));
            });
    };

    return (
        <BCTabContainer title={translate('business.companytab.header')}>
            <div className="BusinessCentral__CompanyTab__container__content">
                <div>
                    <h2 className="BusinessCentral__CompanyTab__container__content__title">
                        {translate('business.companytab.companydetails')}
                    </h2>
                    <BCTextInput
                        label={translate('business.companytab.companyname')}
                        value={company.name}
                    />
                    <BCTextInput
                        label={translate(
                            'business.companytab.organizationnumber'
                        )}
                        value={company.organizationNumber}
                    />
                    <BCTextInput
                        label={translate('business.companytab.vatnumber')}
                        value={company.vatNumber}
                    />
                    <BCTextInput
                        label={translate('business.companytab.address')}
                        value={company.address}
                    />
                    <div className="BusinessCentral__CompanyTab__container__content__split">
                        <BCTextInput
                            label={translate('business.companytab.zipcode')}
                            value={company.zipCode}
                        />
                        <BCTextInput
                            label={translate('business.companytab.city')}
                            value={company.city}
                        />
                    </div>
                    <BCTextInput
                        label={translate('business.companytab.country')}
                        value={company.country}
                    />
                </div>
                <div>
                    <h2 className="BusinessCentral__CompanyTab__container__content__title">
                        {translate('business.companytab.contactinformation')}
                    </h2>
                    <BCTextInput
                        label={translate('business.companytab.firstname')}
                        value={company.contactPerson?.firstName}
                        disabled={false}
                        onChange={(evt) =>
                            onChange('firstName', evt.target.value)
                        }
                        name="firstName"
                        id="firstName"
                    />
                    {errors['firstName'] && (
                        <span className="form__validator--error form__validator--top-narrow">
                            {errors['firstName'][0]}
                        </span>
                    )}
                    <BCTextInput
                        label={translate('business.companytab.lastname')}
                        value={company.contactPerson?.lastName}
                        disabled={false}
                        onChange={(evt) =>
                            onChange('lastName', evt.target.value)
                        }
                        name="lastName"
                        id="lastName"
                    />
                    {errors['lastName'] && (
                        <span className="form__validator--error form__validator--top-narrow">
                            {errors['lastName'][0]}
                        </span>
                    )}
                    <BCTextInput
                        label={translate('business.companytab.email')}
                        value={company.contactPerson?.email}
                    />
                    <BCTextInput
                        label={translate('business.companytab.phone')}
                        value={company.contactPerson?.phone}
                        disabled={false}
                        onChange={(evt) => onChange('phone', evt.target.value)}
                        name="phone"
                        id="phone"
                    />
                    {errors['phone'] && (
                        <span className="form__validator--error form__validator--top-narrow">
                            {errors['phone'][0]}
                        </span>
                    )}
                    <BCTextInput
                        label={translate('business.companytab.costcenter')}
                        value={company.contactPerson?.costCenter}
                        disabled={false}
                        onChange={(evt) =>
                            onChange('costCenter', evt.target.value)
                        }
                        name="costCenter"
                        id="costCenter"
                    />
                    <BCTextInput
                        label={translate('business.companytab.internalorderref')}
                        value={company.contactPerson?.internalOrderRef}
                        disabled={false}
                        onChange={(evt) =>
                            onChange('internalOrderRef', evt.target.value)
                        }
                        name="internalOrderRef"
                        id="internalOrderRef"
                    />
                    {company.contactPerson?.showBudget && (
                        <BCTextInput
                            label={translate('business.companytab.budget')}
                            value={company.contactPerson?.budget}
                        />
                    )}

                    <div className="BusinessCentral__CompanyTab__container__content__checkbox">
                        <input
                            type="checkbox"
                            id="isOrderApprover"
                            checked={company.contactPerson?.isOrderApprover}
                            disabled
                        />
                        <label
                            className="BusinessCentral__CompanyTab__container__content__checkbox__label"
                            htmlFor="isOrderApprover"
                        >
                            {translate('business.companytab.orderapprover')}
                        </label>
                    </div>

                    <button className="form__button" onClick={onSubmit}>
                        {translate('general.save')}
                    </button>
                    {showSuccess && (
                        <span className="form__validator--success">
                            {translate('general.success')}
                        </span>
                    )}
                </div>
            </div>
            <div className="BusinessCentral__CompanyTab__container__password-wrapper">
                <h2 className="BusinessCentral__CompanyTab__container__content__title">
                    {translate('business.companytab.changepassword')}
                </h2>
                <BCTextInput
                    label={translate('business.companytab.password')}
                    value={password.password}
                    id="password"
                    disabled={false}
                    onChange={(evt) =>
                        onChangePassword('password', evt.target.value)
                    }
                    type="password"
                />
                {errors['password'] && (
                    <span className="form__validator--error form__validator--top-narrow">
                        {errors['password'][0]}
                    </span>
                )}
                <BCTextInput
                    label={translate('business.companytab.confirmpassword')}
                    value={password.confirmPassword}
                    id="confirm-password"
                    disabled={false}
                    onChange={(evt) =>
                        onChangePassword('confirmPassword', evt.target.value)
                    }
                    type="password"
                />
                {errors['confirmPassword'] && (
                    <span className="form__validator--error form__validator--top-narrow">
                        {errors['confirmPassword'][0]}
                    </span>
                )}
                <button
                    className="form__button"
                    onClick={onSubmitPassword}
                    disabled={loaderPassword}
                >
                    {translate('general.save')}
                </button>
                {showSuccessPassword && (
                    <span className="form__validator--success">
                        {translate('general.passwordsuccess')}
                    </span>
                )}
            </div>
        </BCTabContainer>
    );
};
